import { useRef, useState } from 'react';
import { useZeroXWithdrawalConfirm, useZeroXWithdrawalPreview } from '../../mutations/useZeroXWithdrawal';
import type { ZeroXWithdrawalInput } from '../../schemas/zeroXWithdrawal';
import {
  INITIAL_WITHDRAWAL_STATE,
  getWithdrawalErrorMessage,
  resolveConfirmationFailure,
} from '../../helpers/payment/zeroXWithdrawal';

export const useZeroXWithdrawalFlow = () => {
  const previewMutation = useZeroXWithdrawalPreview();
  const confirmMutation = useZeroXWithdrawalConfirm();
  const [state, setState] = useState(INITIAL_WITHDRAWAL_STATE);
  const inFlight = useRef(false);

  const { phase } = state;
  const canEdit = phase.name === 'editing' || phase.name === 'reviewing' || phase.name === 'expired';
  const quote = 'quote' in phase ? phase.quote : null;

  const edit = () => {
    if (!canEdit || inFlight.current) {
      return false;
    }

    setState(INITIAL_WITHDRAWAL_STATE);
    return true;
  };

  const preview = async (input: ZeroXWithdrawalInput) => {
    if (inFlight.current || (phase.name !== 'editing' && phase.name !== 'expired')) {
      return;
    }

    inFlight.current = true;
    setState({ phase: { name: 'previewing' }, message: null });

    try {
      const quote = await previewMutation.mutateAsync(input);
      setState({ phase: { name: 'reviewing', quote }, message: null });
    } catch (error) {
      setState({ phase: { name: 'editing' }, message: getWithdrawalErrorMessage(error, input.currency) });
    } finally {
      inFlight.current = false;
    }
  };

  const confirm = async () => {
    if (inFlight.current || (phase.name !== 'reviewing' && phase.name !== 'uncertain')) {
      return;
    }

    const { quote } = phase;
    inFlight.current = true;
    setState({ phase: { name: 'confirming', quote }, message: null });

    try {
      const result = await confirmMutation.mutateAsync({ confirmationToken: quote.confirmationToken });
      setState({ phase: { name: 'done', quote, result }, message: null });
    } catch (error) {
      setState(resolveConfirmationFailure(error, quote));
    } finally {
      inFlight.current = false;
    }
  };

  const reset = () => {
    if (phase.name === 'done' && !inFlight.current) {
      setState(INITIAL_WITHDRAWAL_STATE);
    }
  };

  return { ...state, canEdit, quote, edit, preview, confirm, reset };
};
